import { SubscriptionTier } from '@form-factory/schema/enums';
import { useState, useEffect } from 'react';

import { trpc } from '@/services/trpc';

/**
 * Hook to determine user limits based on active subscription and current user count.
 *
 * @param organizationId The ID of the organization.
 * @returns Object containing limit info and loading state.
 */
export function useSubscriptionUserLimit(organizationId: string | undefined) {
  const [canAddUser, setCanAddUser] = useState(false);
  const [maxUsers, setMaxUsers] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { data: activeSubscriptionData, isLoading: isLoadingSub } =
    trpc.subscriptions.getActive.useQuery(
      { organizationId: organizationId! },
      {
        enabled: !!organizationId,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
      },
    );

  const { data: usersData, isLoading: isLoadingUsers } =
    trpc.users.findAll.useQuery(
      {
        where: { organizations: { id: organizationId! } },
        pagination: { page: 1, pageSize: 1 },
      },
      {
        enabled: !!organizationId,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
      },
    );

  useEffect(() => {
    if (!organizationId || isLoadingSub || isLoadingUsers) {
      setIsLoading(true);

      return;
    }

    const sub = activeSubscriptionData;
    const userCount = usersData?.meta.total ?? 0;
    let currentMaxUsers: number | null = null;
    let canAdd = false;

    if (!sub || sub.tier === SubscriptionTier.Free) {
      // FIXME neměli bychom nastavovat default value i na FE
      currentMaxUsers = sub?.maxUsers ?? 3; // Default 3 for Free
      canAdd = userCount < currentMaxUsers;
    } else {
      // For paid tiers, maxUsers = 0 means unlimited
      currentMaxUsers = sub.maxUsers ?? null; // Use null for unlimited/undefined

      // Handle special case where maxUsers = 0 (unlimited)
      if (currentMaxUsers === 0) {
        currentMaxUsers = null; // Treat as null for display purposes
        canAdd = true; // Unlimited users
      } else {
        canAdd = currentMaxUsers === null ? true : userCount < currentMaxUsers;
      }
    }

    setMaxUsers(currentMaxUsers);
    setCanAddUser(canAdd);
    setIsLoading(false);
  }, [
    organizationId,
    activeSubscriptionData,
    usersData,
    isLoadingSub,
    isLoadingUsers,
  ]);

  return { canAddUser, maxUsers, isLoading: isLoading || !organizationId };
}
